import React, { useEffect, useState, useRef } from "react";
import { UploadIcon, CheckIcon } from "../icons";
import { message, Upload } from "antd";
import axios from "axios";
import {
  getProductLogoUploadUrl,
  updateCampaignLogoApi,
} from "@/api/createCampaignApi";
import { s3ImageLink } from "@/utils/links";
import { useTranslation } from "react-i18next";

const ProductLogo = ({
  src,
  size = 80,
  campaignId,
  updateDetailData,
  isShowUpload,
  setDetails,
  details
}: {
  src: string;
  size?: number;
  campaignId?: string;
  updateDetailData?: Function;
  setDetails?: Function;
  isShowUpload?: boolean;
  details?: any;
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const emptyLogo = `${s3ImageLink}product-logo-empty.svg`;
  const imgSrc = src || emptyLogo;
  const errorRef = useRef(false);
  const roundSize = Math.floor(size / 6);

  // src更新后，重新设置imgSrc
  useEffect(() => {
    const img = new Image();
    img.src = imgSrc;

    // 如果图片已经被缓存，complete 会立即为 true
    if (img.complete) {
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }
    errorRef.current = false;
  }, [src, imgSrc]);

  const [showLogoDone, setShowLogoDone] = useState(false);

  const getAwsUrlAndUpload = async (file: any) => {
    setIsLoading(true);
    // 大小大于10MB
    if (file.size > 10 * 1024 * 1024) {
      message.error(t("components.productLogo.fileSizeCannotExceed10MB"));
      setIsLoading(false);
      return Promise.reject(new Error(t("components.productLogo.fileSizeCannotExceed10MB")));
    }

    try {
      const res = await getProductLogoUploadUrl({ key: file.name });
      const presignedUrl = res?.data?.presignedUrl;
      const publicUrl = res?.data?.publicUrl;
      const options = {
        headers: {
          "Content-Type": file.type,
        },
      };
      await axios.put(presignedUrl, file, options);

      setTimeout(async () => {
        try {
          await updateCampaignLogoApi(campaignId as string, {
            logo: publicUrl,
          });
          setDetails && setDetails({
            ...details,
            product: {
              ...details?.product,
              logo: publicUrl
            }
          });
          updateDetailData && updateDetailData();
          setShowLogoDone(true);
          setTimeout(() => {
            setShowLogoDone(false);
          }, 3000);
          setIsLoading(false);
        } catch (error: any) {
          message.error(t("components.productLogo.failedToUploadLogo"));
          setIsLoading(false);
        }
        return Promise.resolve(presignedUrl);
      }, 0);
    } catch (error: any) {
      message.error(t("components.productLogo.failedToUploadLogo"));
      setIsLoading(false);
    }
  };

  return (
    <div
      className={`box-border border border-[#ededed] overflow-hidden flex flex-shrink-0 justify-center items-center bg-white group/main relative`}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: `${roundSize}px`,
        overflow: "hidden",
      }}
    >
      <img
        alt="logo"
        style={{
          width: "100%",
          maxWidth: `${size}px`,
          maxHeight: `${size}px`,
          display: isLoading ? "none" : "block",
        }}
        src={imgSrc}
        onLoad={(e) => {
          setIsLoading(false);
          const img = e.target as HTMLImageElement;
          if (img?.src.includes(emptyLogo)) {
            return;
          }
          const aspectRatio = Math.abs(img.naturalWidth / img.naturalHeight);
          if (aspectRatio === 1) {
            img.style.padding = "20%"; // 正方形图片 1:1
          } else if (aspectRatio > 2 || aspectRatio < 0.5) {
            img.style.padding = "5%"; // 长宽比 > 2:1 或 < 1:2
          } else {
            img.style.padding = "10%"; // 1:2 <= 比例 <= 2:1
          }
        }}
        onError={(e) => {
          const img = e.target as HTMLImageElement;
          if (!errorRef.current) {
            errorRef.current = true;
            img.src = emptyLogo;
          }
        }}
      />
      {/*  */}
      <div
        className="w-full h-full bg-gray-200 animate-pulse"
        style={{
          display: isLoading ? "block" : "none",
        }}
      />
      {isShowUpload && (
        <div className="absolute left-0 top-0 w-full h-full bg-black/50 items-center justify-center cursor-pointer hidden group-hover/main:flex">
          <Upload
            listType="picture"
            accept=".jpg,.jpeg,.png,.webp,.svg"
            maxCount={1}
            showUploadList={false}
            className="avatar-uploader"
            beforeUpload={getAwsUrlAndUpload}
          >
            <div className="w-8 inline-flex flex-col justify-start items-center">
              <UploadIcon className="text-white" />
              <div className="self-stretch text-center justify-start text-white text-[10px] font-medium  leading-none">
                {t("components.productLogo.upload")}
              </div>
            </div>
          </Upload>
        </div>
      )}
      {
        showLogoDone && <div className="absolute left-0 top-0 w-full h-full bg-black/50 items-center justify-center flex">
          <CheckIcon className="text-white" />
        </div>
      }
    </div>
  );
};

export default ProductLogo;
